import { Select, SelectItem, SelectSection } from '@nextui-org/react'
import { Key } from '@react-types/shared'
import { useEffect, useState } from 'react'
import { SelectIcon } from '~/constants/icon'
import { cm } from '~/utils/utils'

export interface SelectItemType {
  value: string
  label: string
  description?: string
  icon?: React.ReactNode
  disabled?: boolean
}

export interface SelectGroupType {
  title: React.ReactNode
  key: string
  items: SelectItemType[]
  defaultValue?: string
}

interface SelectProps {
  className?: string
  placeholder?: string
  ariaLabel?: string
  size?: 'sm' | 'md' | 'lg'
  selectedKeys: Record<string, string | undefined>
  onSelectionChange: (selections: Record<string, string | undefined>) => void
  isDisabled?: boolean
  isLoading?: boolean
  label?: React.ReactNode
  startContents?: React.ReactNode[]
  isMultiSelect?: boolean
  groups: SelectGroupType[]
}

export function LabeledMultipleCurieSelect({
  className,
  ariaLabel,
  placeholder,
  size = 'sm',
  selectedKeys,
  onSelectionChange,
  isDisabled = false,
  isLoading = false,
  label = '',
  isMultiSelect = false,
  groups
}: SelectProps) {
  const [selectedValues, setSelectedValues] = useState<Set<string>>(
    new Set(Object.values(selectedKeys).filter(value => value != null))
  )
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setSelectedValues(new Set(Object.values(selectedKeys).filter(value => value != null)))
  }, [selectedKeys])

  const valueToGroupMap = new Map<string, string>()
  groups.forEach(group => {
    group.items.forEach(item => {
      valueToGroupMap.set(item.value, group.key)
    })
  })

  const areAllSectionsSelected = () => {
    const selectedGroupKeys = new Set(
      Array.from(selectedValues).map(value => valueToGroupMap.get(value))
    )
    return groups.every(group => selectedGroupKeys.has(group.key))
  }

  const handleSelectionChange = (keys: Set<Key>) => {
    if (Array.from(keys).every(key => selectedValues.has(String(key)))) {
      return
    }

    const newKeys: Record<string, string | undefined> = { ...selectedKeys }

    selectedValues.forEach(value => {
      const groupKey = valueToGroupMap.get(value)
      if (groupKey && !keys.has(value)) {
        delete newKeys[groupKey]
      }
    })

    keys.forEach(value => {
      const groupKey = valueToGroupMap.get(String(value))
      if (groupKey) {
        newKeys[groupKey] = String(value)
      }
    })

    setSelectedValues(new Set(Array.from(keys).map(String)))
    onSelectionChange(newKeys)

    if (isMultiSelect) {
      return
    }

    if (areAllSectionsSelected()) {
      setIsOpen(false)
    }
  }

  return (
    <Select
      labelPlacement='inside'
      radius='md'
      size={size}
      label={label}
      placeholder={placeholder}
      aria-label={ariaLabel || placeholder}
      className={className}
      selectedKeys={selectedValues}
      onSelectionChange={keys => {
        if (keys instanceof Set) {
          handleSelectionChange(keys)
        }
      }}
      isDisabled={isDisabled}
      isLoading={isLoading}
      selectionMode={isMultiSelect ? 'multiple' : 'single'}
      selectorIcon={<SelectIcon />}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      classNames={{
        base: 'w-full border-box dark:bg-medium6 pb-4 px-[1px]',
        trigger: cm(
          'w-full h-full bg-white dark:bg-medium6 data-[hover=true]:bg-light3 dark:data-[hover=true]:bg-medium2 select-none ',
          'shadow-sm ring-1 ring-[#d4dce2] group-data-[focus=true]:bg-transparent rounded-[20px]'
        ),
        innerWrapper: 'truncate',
        popoverContent: 'rounded-xl text-xs dark:bg-medium6',
        label: 'text-sm font-["Geist"] select-none',
        value: 'text-[14px] font-[400] font-["Geist"] select-none',
        selectorIcon: 'w-6 h-6 select-none'
      }}
    >
      {groups.map((group, index) => (
        <SelectSection
          key={group.key}
          // @ts-ignore - NextUI SelectSection actually supports ReactNode for title
          title={group.title}
          classNames={{
            group: cm(
              'px-3',
              index < groups.length - 1 && 'border-b border-gray-200 dark:border-gray-700 pb-2 mb-2'
            ),
            heading:
              "pl-3 label-sm-normal opacity-70 text-black/65 text-[14px] font-[400] font-['Geist'] leading-none select-none"
          }}
        >
          {group.items.map(item => (
            <SelectItem
              key={item.value}
              value={item.value}
              classNames={{
                base: 'px-0 py-2 data-[hover=true]:bg-[#f7f7f7] dark:data-[hover=true]:bg-gray-800',
                title: 'text-[14px] font-[400] font-["Geist"] select-none',
                description: 'select-none',
                wrapper: 'flex !flex-row items-center gap-2',
                selectedIcon: '!order-first'
              }}
              startContent={item.icon}
              description={item.description}
            >
              {item.label}
            </SelectItem>
          ))}
        </SelectSection>
      ))}
    </Select>
  )
}
